import React from "react";
import {
  FileText,
  UserCheck,
  Upload,
  Copyright,
  ShieldAlert,
  Ban,
  Scale,
  AlertTriangle,
  RefreshCw,
  Gavel,
  Mail,
  Phone,
  Globe,
} from "lucide-react";

const TermsOfService = () => {
  const sections = [
    {
      id: "use-of-services",
      number: "01",
      title: "Use of Services",
      icon: UserCheck,
      content: (
        <ul className="space-y-3 text-slate-600">
          <li>
            You must meet any applicable age and legal requirements to use our
            services.
          </li>

          <li>
            You agree to use Nexindi Tech products and services lawfully,
            responsibly, and in accordance with these Terms.
          </li>

          <li>
            You are responsible for maintaining the confidentiality of your
            account credentials and for activity performed through your account.
          </li>

          <li>
            You agree to provide accurate information when creating an account,
            requesting services, making purchases, or communicating with us.
          </li>
        </ul>
      ),
    },
    {
      id: "user-content",
      number: "02",
      title: "User Content",
      icon: Upload,
      content: (
        <p className="text-slate-600 leading-relaxed">
          Certain Nexindi Tech services may allow you to submit, upload, post,
          or share content. You retain ownership of content you create, but you
          grant Nexindi Tech the permissions reasonably necessary to host,
          process, display, transmit, or otherwise use that content for the
          purpose of providing the relevant service.
        </p>
      ),
    },
    {
      id: "intellectual-property",
      number: "03",
      title: "Intellectual Property",
      icon: Copyright,
      content: (
        <p className="text-slate-600 leading-relaxed">
          Unless otherwise stated, the Nexindi Tech name, branding, website
          content, software, designs, graphics, documentation, and other
          materials made available through our services are owned by Nexindi
          Tech or used with permission. You may not copy, reproduce, modify,
          distribute, sell, or commercially exploit protected materials without
          authorization.
        </p>
      ),
    },
    {
      id: "prohibited-activities",
      number: "04",
      title: "Prohibited Activities",
      icon: ShieldAlert,
      content: (
        <div className="text-slate-600">
          <p className="mb-4">
            When using our services, you must not:
          </p>

          <ul className="space-y-3">
            <li>Use our services for fraudulent, unlawful, or abusive activity.</li>
            <li>
              Attempt to gain unauthorized access to accounts, systems, servers,
              databases, or networks.
            </li>
            <li>
              Introduce malware, viruses, harmful code, or other technologies
              intended to disrupt our systems.
            </li>
            <li>
              Interfere with the security, availability, or operation of our
              products and services.
            </li>
            <li>
              Use our services in a way that violates the rights of other
              individuals or organizations.
            </li>
          </ul>
        </div>
      ),
    },
    {
      id: "payments",
      number: "05",
      title: "Payments, Orders and Services",
      icon: FileText,
      content: (
        <div className="text-slate-600 space-y-4">
          <p>
            Some Nexindi Tech products and services require payment. Prices,
            project quotations, subscription fees, product costs, and other
            charges may vary depending on the service or project scope.
          </p>

          <p>
            Payment processing may be handled through third-party payment
            providers. By making a payment, you may also be subject to the terms
            and policies of the applicable payment provider.
          </p>

          <p>
            For custom software and professional services, timelines, scope,
            deliverables, payment schedules, ownership arrangements, and other
            project-specific terms may be governed by a separate quotation,
            agreement, proposal, or contract.
          </p>
        </div>
      ),
    },
    {
      id: "termination",
      number: "06",
      title: "Suspension and Termination",
      icon: Ban,
      content: (
        <p className="text-slate-600 leading-relaxed">
          We may suspend, restrict, or terminate access to our services where
          reasonably necessary, including where a user violates these Terms,
          engages in unlawful or harmful activity, threatens platform security,
          or misuses our products or services.
        </p>
      ),
    },
    {
      id: "disclaimers",
      number: "07",
      title: "Disclaimers",
      icon: AlertTriangle,
      content: (
        <p className="text-slate-600 leading-relaxed">
          Our services are provided on an "as available" basis to the extent
          permitted by applicable law. While we work to provide reliable and
          secure services, we do not guarantee that every service will always
          be uninterrupted, error-free, or available without technical issues.
        </p>
      ),
    },
    {
      id: "liability",
      number: "08",
      title: "Limitation of Liability",
      icon: Scale,
      content: (
        <p className="text-slate-600 leading-relaxed">
          To the extent permitted by applicable law, Nexindi Tech will not be
          responsible for indirect, incidental, special, or consequential losses
          arising from the use of or inability to use our services. Nothing in
          these Terms excludes liability that cannot legally be excluded.
        </p>
      ),
    },
    {
      id: "changes",
      number: "09",
      title: "Changes to These Terms",
      icon: RefreshCw,
      content: (
        <p className="text-slate-600 leading-relaxed">
          We may update these Terms from time to time to reflect changes in our
          products, business practices, technology, or legal requirements. The
          updated version will be published on this page with a revised
          effective date. Continued use of our services after an update may
          constitute acceptance of the revised Terms.
        </p>
      ),
    },
    {
      id: "governing-law",
      number: "10",
      title: "Governing Law",
      icon: Gavel,
      content: (
        <p className="text-slate-600 leading-relaxed">
          These Terms are governed by and interpreted in accordance with the
          laws of Kenya, subject to any mandatory rights or protections that
          apply under applicable law.
        </p>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-24 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <FileText size={15} />
            Legal Terms
          </div>

          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Terms of Service
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            These Terms explain the rules and responsibilities that apply when
            you access Nexindi Tech websites, products, platforms, and services.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400">
            <span>Effective Date:</span>

            <strong className="text-white">
              May 26, 2025
            </strong>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="px-6 py-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[270px_1fr] gap-10 xl:gap-16">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-400 font-bold mb-5">
                On this page
              </p>

              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                  >
                    {section.number}. {section.title}
                  </a>
                ))}

                <a
                  href="#contact"
                  className="block px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                >
                  11. Contact Us
                </a>
              </nav>
            </div>
          </aside>

          {/* Terms */}
          <div className="max-w-4xl">
            {/* Intro */}
            <div className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9 shadow-xl shadow-slate-900/5 mb-8">
              <p className="text-lg text-slate-600 leading-relaxed">
                Welcome to{" "}
                <strong className="text-slate-900">
                  Nexindi Tech Company
                </strong>
                . By accessing or using our website, products, platforms, or
                services, you agree to comply with these Terms of Service.
                Please read them carefully before using our services.
              </p>
            </div>

            <div className="space-y-6">
              {sections.map((section) => {
                const Icon = section.icon;

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-28 bg-white border border-slate-200 rounded-3xl p-7 md:p-9"
                  >
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <Icon size={21} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xs font-bold text-blue-600">
                            {section.number}
                          </span>

                          <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                            {section.title}
                          </h2>
                        </div>

                        {section.content}
                      </div>
                    </div>
                  </section>
                );
              })}

              {/* Contact */}
              <section
                id="contact"
                className="scroll-mt-28 relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 p-7 md:p-9 text-white"
              >
                <div className="absolute -top-24 -right-20 w-72 h-72 bg-cyan-300/10 rounded-full blur-[90px]" />

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs font-bold text-blue-200">
                      11
                    </span>

                    <h2 className="text-2xl font-bold">
                      Contact Us
                    </h2>
                  </div>

                  <p className="text-blue-100 leading-relaxed max-w-2xl mb-7">
                    If you have any questions about these Terms of Service,
                    please contact Nexindi Tech through any of the channels
                    below.
                  </p>

                  <div className="grid md:grid-cols-3 gap-4">
                    <a
                      href="mailto:nexinditechcompany@gmail.com"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Mail size={18} className="text-blue-200" />

                      <div className="min-w-0">
                        <p className="text-xs text-blue-200 mb-1">
                          Email
                        </p>

                        <p className="text-sm font-semibold truncate">
                          nexinditechcompany@gmail.com
                        </p>
                      </div>
                    </a>

                    <a
                      href="tel:+254797655727"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Phone size={18} className="text-blue-200" />

                      <div>
                        <p className="text-xs text-blue-200 mb-1">
                          Phone
                        </p>

                        <p className="text-sm font-semibold">
                          +254 797 655 727
                        </p>
                      </div>
                    </a>

                    <a
                      href="https://nexinditechcompany.online/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Globe size={18} className="text-blue-200" />

                      <div>
                        <p className="text-xs text-blue-200 mb-1">
                          Website
                        </p>

                        <p className="text-sm font-semibold">
                          nexinditechcompany.online
                        </p>
                      </div>
                    </a>
                  </div>
                </div>
              </section>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed mt-8">
              These Terms provide general conditions governing use of Nexindi
              Tech services. Individual products, professional services,
              software projects, or commercial engagements may also be governed
              by additional agreements or terms.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;